import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const cards = [
  {
    eyebrow: 'The agency model',
    label: 'Slow, layered, expensive.',
    body: 'Account managers, strategists, planners, buyers — and a markup on each. Three-week turnarounds before the first dollar performs.',
    tone: 'muted',
  },
  {
    eyebrow: 'The in-house model',
    label: 'Hire. Train. Manage. Wait.',
    body: 'Months of recruiting, ramping, and management before you see a single result — plus six-figure salaries and tools to keep the team effective.',
    tone: 'muted',
  },
  {
    eyebrow: 'The Sellrly model',
    label: 'Agency-grade. Zero bureaucracy.',
    body: 'Direct access to senior media operators. Net-new audiences across the open web. We start small, prove the model, then scale.',
    tone: 'highlight',
  },
];

export default function WhyItWorks({ id = 'why-it-works', titleCase = false }) {
  return (
    <section id={id} className="relative py-24 md:py-32 bg-white text-[#031D44]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-4xl">
          <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.22em] text-[#FF5081]">Why it works</p>
          <h2 className={`mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.025em] leading-[1.02] text-[#031D44] ${titleCase ? 'capitalize' : ''}`}>
            Most growth options force a bad trade.
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map((c, i) => (
            <motion.div
              key={c.eyebrow}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className={
                c.tone === 'highlight'
                  ? 'relative rounded-2xl p-8 md:p-9 bg-[#0E103D] text-white border border-[#0E103D]'
                  : 'relative rounded-2xl p-8 md:p-9 bg-[#F8F7FC] border border-[#031D44]/8'
              }
            >
              <p className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${c.tone === 'highlight' ? 'text-[#FF5081]' : 'text-[#031D44]/45'}`}>
                {c.eyebrow}
              </p>
              <h3 className={`mt-3 font-display text-2xl md:text-[26px] font-semibold tracking-tight leading-snug ${c.tone === 'highlight' ? 'text-white' : 'text-[#031D44]'}`}>
                {c.label}
              </h3>
              <p className={`mt-4 leading-relaxed text-[15px] ${c.tone === 'highlight' ? 'text-white/70' : 'text-[#031D44]/60'}`}>
                {c.body}
              </p>
              {c.tone === 'highlight' && (
                <div className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF5081]">
                  The Sellrly difference <ArrowRight className="w-3.5 h-3.5" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Moved-below outro */}
        <div className="mt-14 max-w-3xl">
          <p className="text-lg lg:text-xl leading-relaxed text-[#031D44]/70">
            Sellrly is the best option. Agency-grade execution without agency bureaucracy — with the output of an in-house team without the in-house headcount.
          </p>
        </div>
      </div>
    </section>
  );
}
